import { initSentry } from '@repo/sentry-config/frontend'

if (import.meta.env.VITE_FRONTEND_SENTRY_DSN) {
  initSentry(
    import.meta.env.VITE_FRONTEND_SENTRY_DSN,
    import.meta.env.VITE_FRONTEND_ENV,
    import.meta.env.VITE_SENTRY_RELEASE,
  )
}

import { nonRetryableErrorCodes } from '@repo/constants'
import { SwitchableModule, UserPermission } from '@repo/permissions'
import { MutationCache, QueryClient } from '@tanstack/react-query'
import { createRouter } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import App from './App'
import { RouteStaticDataPermissions } from './hooks/permissions/usePermissions'
import './index.css'
import { routeTree } from './routeTree.gen'

export const queryClient: QueryClient = new QueryClient({
  // A CRT version reads as out of date as soon as one of its drivers changes, but the versions list is cached
  // for minutes. Any committed change may be a driver's (margins, rates, assumptions…), and listing which ones are
  // would drift, so every successful mutation marks the list stale: the screens showing it refetch at once, the
  // others on their next mount. Imports land later, from the worker; the configure dialog and the versions page
  // refetch when they open for those.
  mutationCache: new MutationCache({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['crtVersions'] })
    },
  }),
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if ('status' in error && typeof error.status === 'number') {
          return nonRetryableErrorCodes.has(error.status) ? false : failureCount < 3
        }
        return failureCount < 3
      },
    },
  },
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: {
    queryClient,
    hasRoutePermissions: () => true,
    hasRouteOneOfPermissions: () => true,
    handleRoutePermissions: () => undefined,
    assertModuleAccess: () => undefined,
  },
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
})

export type RouterContext = {
  queryClient: QueryClient
  hasRoutePermissions: (
    permissions: UserPermission[],
    route: RouteStaticDataPermissions,
  ) => boolean | void
  hasRouteOneOfPermissions: (
    permissions: UserPermission[],
    route: RouteStaticDataPermissions,
  ) => boolean | void
  handleRoutePermissions: (route: RouteStaticDataPermissions) => void
  assertModuleAccess: (module: SwitchableModule) => void
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App router={router} queryClient={queryClient} />
  </StrictMode>,
)
